import { DateTime } from 'luxon'

const isLastDayOfMonth = (date: DateTime): boolean =>
  date.day === date.endOf('month').day

const parseDateInput = (value: string): DateTime => {
  return DateTime.fromISO(value)
}

const isDateInAllowedDates = (
  date: DateTime,
  allowedDates?: Date[]
): boolean => {
  if (!date.isValid) {
    return false
  }

  if (!allowedDates) {
    return true
  }

  return allowedDates.some((allowedDate) =>
    DateTime.fromJSDate(allowedDate).hasSame(date, 'day')
  )
}

const isDateWithinRange = (
  date: DateTime,
  from: DateTime,
  to: DateTime
): boolean => {
  const dateIso = date.toISODate()
  const fromIso = from.toISODate()
  const toIso = to.toISODate()

  if (!dateIso || !fromIso || !toIso) {
    return false
  }

  return fromIso <= dateIso && dateIso <= toIso
}

export const isAllowedMonthEndDate = (
  value: string,
  allowedDates?: Date[]
): boolean => {
  const date = parseDateInput(value)
  if (!date.isValid || !isLastDayOfMonth(date)) {
    return false
  }

  return isDateInAllowedDates(date, allowedDates)
}

export const isAllowedMonthEndDateWithinRange = (
  value: string,
  from: DateTime,
  to: DateTime,
  allowedDates?: Date[]
): boolean => {
  const date = parseDateInput(value)
  if (
    !date.isValid ||
    !isDateWithinRange(date, from, to) ||
    !isLastDayOfMonth(date)
  ) {
    return false
  }

  return isDateInAllowedDates(date, allowedDates)
}

export const getAllowedMonthEndDates = (
  from: DateTime,
  to: DateTime
): Date[] => {
  const fromIso = from.toISODate()
  const toIso = to.toISODate()

  if (!fromIso || !toIso || fromIso > toIso) {
    return []
  }

  const allowedDates: Date[] = []

  for (
    let currentMonth = from.startOf('month');
    currentMonth.toISODate()! <= toIso;
    currentMonth = currentMonth.plus({ months: 1 })
  ) {
    const monthEnd = currentMonth.endOf('month')
    const monthEndIso = monthEnd.toISODate()

    if (monthEndIso && monthEndIso >= fromIso && monthEndIso <= toIso) {
      allowedDates.push(monthEnd.toJSDate())
    }
  }

  return allowedDates
}
